/* eslint-disable prefer-object-spread */
/* eslint-disable radix */
/* eslint-disable no-param-reassign */
/* eslint-disable no-useless-constructor */
import { OMT_Utils } from './utils/OMT_Utils';

const OMT_DATA_KEY = 'gmdatastring-cc2';

// keys to delete from migrated data
const KEYS_TO_DELETE = [
  'authData', 'botOptin', 'botOptIn', 'ddna-sendingQueue', 'ddnaAdTracker', 'ddnaTransactionTracker',
  'ftueEvents', 'globalGoals', 'hintsDisabled', 'levelRetries', 'levelTrackerData', 'levelTrackingStreak',
  'saveTimestamp', 'seenReferralPopup', 'userMetaData', 'tutorialStarsProgressCompleteSeen',
  'tutorialStarsProgressIntroSeen', 'ftueTutorialPopupsSeen', 'ftueTutorialLevelPopupsSeen',
  'unlockedPreLevelBoostersTmp',
];

// keys used for legacy FB storage
const FB_STORAGE_KEYS = [
  'coins', 'lives', 'saveTimestamp', 'lastRefillDate', 'lastRefillDate', 'lastDaily',
  'freeSpin', 'levels', 'points', 'gates', 'mapChests', 'boosters', 'finishedTutorials',
  'startBoosterAnim', 'resourceSentTimestamp', 'mute', 'muteFx', 'version', 'loginTrack',
  'loginTrack', 'mysteryGift', 'extraStars', 'treasureHunt', 'unlimitedLives', 'popUpTutorials',
  'bubbleGifts', 'dailyMissionDate', 'dailyMissions', 'lastChallengeTry', 'lastMoneySpin',
  'dailyReward', 'botMsgs', 'unlimitedLivesStart', 'dailyBeaten', 'levelRetries',
  'payloadGifts', 'acceptedLives', 'acceptedLivesRequests', 'quiz', 'levelsMapPositions', 'order',
];

// keys to be checked to detect legacy data in 'gmdatastring-cc2' Object
const LEGACY_DETECT_KEYS = [
  'saveTimestamp', 'userMetaData', 'ddna-sendingQueue', 'ddnaAdTracker', 'ddnaTransactionTracker',
];

/**
 * class for handling CC2 -> OMT data migration
 */
export class OMT_LegacyCC2DataHandler {
  /**
   * constructor
   */
  constructor() {
    this._lsCC2Data = null;
    this._fbCC2Data = null;
  }

  /**
   * load legacy CC2 data from alt data sources.
   */
  async loadLegacyCC2Data() {
    //if (OMT.systemInfo.browserIsIE) return; // we don't want to load legacy data for IE 11

    this._lsCC2Data = this._getLegacyCC2LocalData();
    this._fbCC2Data = await this._getLegacyCC2FBData();
  }

  /**
   * check if the game has legacy CC2 data
   * @returns {boolean}
   */
  isLegacyCC2ImportRequired(gbData) {
    //if (OMT.systemInfo.browserIsIE) return false; // we don't want to load legacy data for IE 11

    const gbDataIsCookData = this._isLegacyCC2DataObj(gbData);
    const fbDataIsCookData = this._isLegacyCC2DataObj(this._fbCC2Data);
    const lsDataIsCookData = this._isLegacyCC2DataObj(this._lsCC2Data);
    const hasOMTData = this._hasOMTDataObj(gbData[OMT_DATA_KEY]);
    const CC2ImportRequired = !hasOMTData && (gbDataIsCookData || fbDataIsCookData || lsDataIsCookData);

    // log data import state
    OMT_Utils.stylizedLog('---=== CC2 DATA IMPORT STATUS ===---', '#ffb3ff');
    OMT_Utils.stylizedLog(`userHasLegacyCC2Data = ${gbDataIsCookData || fbDataIsCookData || lsDataIsCookData}`, '#ffb3ff');
    OMT_Utils.stylizedLog(`hasOMTData = ${hasOMTData}, CC2ImportRequired =  ${CC2ImportRequired}`, '#ffb3ff');
    return CC2ImportRequired;
  }

  /**
   * check if the data Object has been converted to OMT
   * @param {Object} saveKeyData
   * @returns {boolean}
   */
  _hasOMTDataObj(saveKeyData) {
    if (saveKeyData == null) return false;
    const saveKeydataType = typeof saveKeyData;
    // authData + ftueEvents should only be present on legacy data
    return saveKeydataType === 'object' && saveKeyData.authData == null && saveKeyData.ftueEvents == null;
  }

  /**
   * check if the data Object is cookie crush data.
   * @param {Object} data
   * @returns {boolean}
   */
  _isLegacyCC2DataObj(data) {
    if (data == null) return false;
    const saveKeyData = data[OMT_DATA_KEY];
    const saveKeydataType = typeof saveKeyData;
    // check save data for old CC2 keys. Some users seem to have messy userData states.
    if (saveKeyData != null && saveKeydataType === 'object') {
      if (Object.keys(saveKeyData).length === 0) return false;
      for (let i = 0; i < LEGACY_DETECT_KEYS.length; i++) {
        const legacyKey = LEGACY_DETECT_KEYS[i];
        if (saveKeyData[legacyKey] != null) return true;
      }
    } else if (saveKeyData != null && saveKeydataType === 'string') { // check if saveKeyData is in old serialized format
      return true;
    }
    // no nested saveData key + data Object has other data
    return data.levelRetries != null && saveKeyData == null;
  }

  /**
   * get legacy CC2 data from FB storage.
   * @returns {Object}
   */
  async _getLegacyCC2FBData() {
    let fbData = null;
    try {
      fbData = null;//await FBInstant.player.getDataAsync(FB_STORAGE_KEYS);
    } catch (error) {
      fbData = null;
    }
    return fbData;
  }

  /**
   * delete FB data
   * @returns {Promise}
   */
  async _deleteLegacyCC2FBData() {
    const data = {};
    FB_STORAGE_KEYS.forEach((paramName) => {
      data[paramName] = null;
    });
    //await FBInstant.player.setDataAsync(data);
  }

  /**
   * get legacy CC2 data from local storage if it exists.
   * @returns {Object}
   */
  _getLegacyCC2LocalData() {
    const legacyLSKey = `userData_${OMT.envData.settings.user.userId}`;
    let legacyLSData;
    try {
      legacyLSData = localStorage.getItem(legacyLSKey);
      if (JSONUtil.isJson(legacyLSData)) legacyLSData = JSON.parse(legacyLSData);
    } catch (error) {
      legacyLSData = null;
    }
    return legacyLSData;
  }

  /**
   * delete obsolete CC2 local data
   */
  _deleteLegacyCC2LocalData() {
    const legacyLSKey = `userData_${OMT.envData.settings.user.userId}`;
    try {
      localStorage.removeItem(legacyLSKey);
    } catch (error) {
      //
    }
  }

  /**
   * delete obsolete data stores. Not used yet, but will be used for data cleanup.
   */
  deleteLegacyCC2Data() {
    // TODO: we will need to delete legacy keys as well.
    this._deleteLegacyCC2LocalData();
    this._deleteLegacyCC2FBData();
  }

  /**
   * filter the data sources and return the latest saved data source
   * @param {Array.<Object>} dataSources
   * @returns {Object}
   */
  _getLatestCC2Data(dataSources) {
    // filter only valid data sources
    let validDataSources = dataSources.filter((data) => data != null && data.levels != null);
    if (validDataSources.length === 0) return null;

    // sort by timestamp, only used for comparison for logging
    validDataSources.sort((data_1, data_2) => {
      const timestamp_1 = parseInt(data_1.saveTimestamp);
      const timestamp_2 = parseInt(data_2.saveTimestamp);
      return timestamp_2 - timestamp_1;
    });
    const latestTimestamp = parseInt(validDataSources[0].saveTimestamp);

    // primary sort on most levels
    validDataSources.sort((data_1, data_2) => {
      const levelCount_1 = data_1.levels.length;
      const levelCount_2 = data_2.levels.length;
      return levelCount_2 - levelCount_1;
    });

    // filter list to only entries on the highest level
    const higestLevelCount = validDataSources[0].levels.length;
    validDataSources = validDataSources.filter((data) => data.levels.length === higestLevelCount);
    // secondary sort on stars
    validDataSources.sort((data_1, data_2) => {
      const starCount_1 = this._calculateTotalStars(data_1);
      const starCount_2 = this._calculateTotalStars(data_2);
      return starCount_2 - starCount_1;
    });

    const prefferedData = validDataSources[0];
    // if prefferedData is not the latest available send a tracking event
    if (latestTimestamp !== parseInt(prefferedData.saveTimestamp)) {
      OMT.platformTracking.logEvent('userMigratedOldSaveSelected');
    }
    return prefferedData;
  }

  /**
   * get total stars for a userData Object
   * @param {Object} dataObj userData Object
   */
  _calculateTotalStars(dataObj) {
    const levels = dataObj.levels ? dataObj.levels : [];
    let totalStars = 0;
    for (let i = 0; i < levels.length; i++) totalStars += levels[i] || 0;
    return totalStars + (dataObj.extraStars ? dataObj.extraStars : 0);
  }

  /**
   * convert Cookie Crush save data to OMT save data
   * @param {Object} gbCC2Data
   * @returns {Object}
   */
  convertCC2toOMTData(gbCC2Data) {
    // convert data that was set before COOK Legacy changed their data format.
    if (gbCC2Data.levelRetries == null && typeof gbCC2Data[OMT_DATA_KEY] === 'string') {
      gbCC2Data = Object.assign({}, gbCC2Data, JSON.parse(gbCC2Data[OMT_DATA_KEY]));
    }
    const latestCC2Data = this._getLatestCC2Data([gbCC2Data, this._lsCC2Data, this._fbCC2Data]);
    const omtData = Object.assign({}, latestCC2Data);
    this._convertLevelRetryData(latestCC2Data, omtData);
    this._convertLoginTrack(latestCC2Data, omtData);
    this._deleteParams(omtData, KEYS_TO_DELETE);
    // we need to flag that this user originated from COOK
    omtData.migratedFromCook = true;
    G.firstTime = false;
    return omtData;
  }

  /**
   * convert the loginTrack Object to the OMT format.
   * @param {Object} cookData
   * @param {Object} omtData
   */
  _convertLoginTrack(cookData, omtData) {
    omtData.loginTrack = {
      consecutiveLoginDays: cookData.consecutiveDays || 0,
      count: cookData.count || 0,
      creation: cookData.creation || Date.now(),
      dailyCount: cookData.dailyCount || 0,
      firstDay: cookData.firstDay || false,
      lastDate: cookData.lastDate || Date.now(),
    };
  }

  /**
   * convert the level retry counts to the OMT format.
   * @param {Object} cookData
   * @param {Object} omtData
   */
  _convertLevelRetryData(cookData, omtData) {
    omtData.retryCounts = {};
    if (cookData.levelRetries) {
      cookData.levelRetries.forEach((value, index) => {
        if (index === 0 || value === null) return; // their data starts at index 1 for some reason.
        omtData.retryCounts[`l_${index - 1}`] = value;
      });
    }
  }

  /**
   * delete outdated parameters from the save data
   * @param {Object} data
   * @param {Array.<string>} paramList
   */
  _deleteParams(data, paramList) {
    paramList.forEach((paramName) => {
      delete data[paramName];
    });
  }
}
